'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Pizza, Beer, Check, X, Clock, Loader2 } from 'lucide-react';

export default function SupplyHeader() {
  const [requests, setRequests] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const fetchRequests = async () => {
    try {
      const res = await fetch('/api/supply');
      const data = await res.json();
      setRequests(data);
    } catch(e) {}
  };

  useEffect(() => {
    fetchRequests();
    const interval = setInterval(fetchRequests, 2000);
    return () => clearInterval(interval);
  }, []);

  const activeCount = requests.filter(r => r.status !== 'completed').length;

  const updateStatus = async (id: string) => {
    await fetch('/api/supply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'update', id })
    });
    fetchRequests();
  };

  const deleteRequest = async (id: string) => {
    await fetch('/api/supply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', id })
    });
    fetchRequests();
  };

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
          activeCount > 0 
            ? 'bg-amber-600 border-amber-500 text-white animate-pulse' 
            : 'bg-slate-900 border-slate-800 text-slate-500 hover:text-white'
        }`}
      >
        <ShoppingCart size={18} />
        <span className="font-bold text-sm">
          {activeCount > 0 ? `${activeCount} Offen` : 'Logistik'}
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-[99]" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="absolute right-0 top-full mt-2 w-80 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-4 z-[100] max-h-[80vh] overflow-y-auto"
            >
              <h3 className="text-white font-bold mb-3 border-b border-slate-800 pb-2">Aktuelle Bestellungen</h3>
              
              <div className="space-y-2">
                {requests.length === 0 && <p className="text-slate-500 text-center text-xs py-4">Keine Bestellungen.</p>}
                
                {requests.map(req => (
                  <div key={req.id} className={`p-2 rounded border border-slate-800 bg-slate-950/50 flex items-start gap-3 ${req.status === 'completed' ? 'opacity-50' : ''}`}>
                    <div className={`p-2 rounded-full ${req.type === 'drink' ? 'bg-blue-900 text-blue-400' : 'bg-orange-900 text-orange-400'}`}>
                      {req.type === 'drink' ? <Beer size={14} /> : <Pizza size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-300">{req.user}</span>
                        <span className="text-[10px] text-slate-600">{new Date(req.timestamp).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                      </div>
                      <p className="text-sm font-medium text-white truncate">{req.item}</p>
                      <p className="text-[10px] uppercase font-bold tracking-wider mt-1" style={{ color: req.status === 'pending' ? '#f59e0b' : req.status === 'delivering' ? '#3b82f6' : '#22c55e' }}>
                        {req.status === 'pending' ? 'Warten' : req.status === 'delivering' ? 'Unterwegs' : 'Erledigt'}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      {req.status !== 'completed' && (
                        <button onClick={() => updateStatus(req.id)} className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-green-500">
                           {req.status === 'pending' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        </button>
                      )}
                      <button onClick={() => deleteRequest(req.id)} className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-red-500">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
